import { AccessContext, AccessControl } from "@components/AccessControl/AccessControl";
import { Body } from "@components/Body/Body";
import { JMCBreadcrumb } from "@components/JMCBreadcrumb/JMCBreadcrumb";
import { JMCHtml } from "@components/JMCHtml/JMCHtml";
import { MedicalControl } from "@components/MedicalControl/MedicalControl";
import { PageHeader } from "@components/PageHeader/PageHeader";
import PaywallTeaser from "@components/PaywallTeaser/PaywallTeaser";
import { SEO } from "@components/SEO/SEO";
import { AuthDataProvider } from "@contexts";
import { FootnotesProvider } from "@contexts";
import { FlexibleGrid } from "@jmc/solid-design-system/src/components/atoms/FlexibleGrid/FlexibleGrid";
import { SnackBar } from "@jmc/solid-design-system/src/components/atoms/SnackBar/SnackBar";
import { noTopPaddingFirstSection } from "@jmc/solid-design-system/src/components/molecules/HeaderHero/utils";
import { HEADING_BACKGROUNDS } from "@jmc/solid-design-system/src/components/molecules/Heading/Heading";
import { useJnjBranding } from "@jmc/utils/hooks/useJnjBranding";
import { mdiInformationOutline } from "@mdi/js";
import { ValidAuthResults } from "@redux/modules/authValid";
import {
    AccessLevel,
    CMS_HEADER_SOURCE_CAROUSEL,
    CMS_HEADER_SOURCE_COLORED_BACKGROUND,
    CMS_HEADER_SOURCE_HERO,
    CMS_HEADER_SOURCE_IMAGE,
    CMS_HEADER_SOURCE_INHERIT_FROM_SITE,
    CMS_HEADER_SOURCE_NONE,
    CMS_MEDICAL_CONTENT,
    CMS_PROMOTIONAL_CONTENT,
    CMSEventProps,
    CMSFragmentProps,
    CMSFreeFormPageSiteReference,
    CMSLegalDocuments,
    CMSPage,
    CMSPersonProps,
    CMSProductProps,
    CMSSpecialtyProps,
    IMR,
    NumericAccessLevel,
    PageContext,
} from "@types";
import { isAuthorizedForProductColors } from "@utils/authorizer";
import { mapFaqSEO } from "@utils/cms-mapper/mapSEOProps";
import isEqual from "lodash/isEqual";
import React, { useContext, useEffect, useState } from "react";
import { useTranslation, WithTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { CMSFaqProps } from "types/CMSFaqProps";
import yn from "yn";

import { QuickEventsContext } from "../contexts/QuickEventsContext/index";
import { collectDeep } from "../search/utils/transformer-utils";
import { DefaultServiceProperties, getDefaultServiceProperties } from "../utils/DefaultServiceProperties";
import style from "./freeform-page.module.scss";

interface PropTypes extends WithTranslation {
    data: {
        page: CMSPage;
        legalDocuments: CMSLegalDocuments;
        productGlossary: { enable_service: boolean };
        specialtyService: { enable_service: boolean };
    };
    pageContext: PageContext;
    authenticated?: boolean;
    authData?: ValidAuthResults;
}

export const FreeFormPage = (props: PropTypes): JSX.Element => {
    const authData = useSelector(
        ({ authValid }: { authValid: { data: ValidAuthResults } }) => authValid?.data,
        isEqual,
    );
    const { t } = useTranslation();
    const { jnjFullBranded } = useJnjBranding();
    const { site_reference } = props.data.page;
    const siteReference: CMSFreeFormPageSiteReference = site_reference && site_reference.length && site_reference[0];
    const relatedTo: CMSProductProps | CMSSpecialtyProps | CMSEventProps = siteReference && siteReference.relation[0];
    const isProductPage = (): boolean => {
        return relatedTo?.__typename === "Contentstack_product";
    };

    const {
        data: {
            page: {
                uid,
                page_title,
                title,
                table_of_content,
                seo_settings,
                content,
                regulatory_status,
                header_visual,
                access_control: { access_level },
                fields,
                smpc_reference,
            },
            productGlossary,
            specialtyService,
        },
        pageContext,
    } = props;
    const {
        url,
        gatedUrl,
        locale,
        page: { cvents, quickEvents },
    } = pageContext;
    const [shouldAddProductTheme, setShouldAddProductTheme] = useState(
        access_level === AccessLevel.level1 && !isProductPage(),
    );
    const [showPaywallTeaser, setShowPaywallTeaser] = useState(false);

    //using this flag to prevent the AE/PI Disclaimer showing up for sometime initially in paywalled page when not logged in
    const [pageDataObtained, setPageDataObtained] = useState(false);

    const show_AE_PI_Disclaimer =
        (showPaywallTeaser && authData && authData?.results?.[0]?.level !== NumericAccessLevel.level1) ||
        (!showPaywallTeaser && pageDataObtained);

    useEffect(() => {
        if (!pageDataObtained) {
            setPageDataObtained(true);
        }
    });

    useEffect(() => {
        if (authData?.results?.[0]) {
            setShowPaywallTeaser(false);
        }
    }, [authData]);

    const listenerFunction = (): void => {
        const { page, gatedUrl, url } = pageContext;
        setShouldAddProductTheme(
            isAuthorizedForProductColors(
                authData,
                page?.access_control?.access_level,
                isProductPage(),
                gatedUrl ?? url,
            ),
        );
    };

    useEffect(() => {
        const { page, gatedUrl, url } = pageContext;
        const direct =
            typeof window !== "undefined"
                ? window?.previousPath === "" ||
                  (!window?.previousPath?.includes(process.env.GATSBY_APP_DOMAIN) &&
                      !window?.previousPath?.includes("webaccess-janssen.com") &&
                      !window?.previousPath?.includes("pre-allow"))
                : true;
        if (direct && !pageContext.commercial_content) {
            document.addEventListener("disclaimerAccepted", listenerFunction);
        } else {
            setShouldAddProductTheme(
                isAuthorizedForProductColors(
                    authData,
                    page?.access_control?.access_level,
                    isProductPage(),
                    gatedUrl ?? url,
                ),
            );
        }
        return (): void => {
            if (direct) {
                document.removeEventListener("disclaimerAccepted", listenerFunction);
            }
        };
    }, [authData, pageContext]);

    const ae_pi_disclaimer = siteReference?.ae_pi_disclaimer || "";

    const commercial = regulatory_status.promotional_or_medical === CMS_PROMOTIONAL_CONTENT;

    let headerVisual = null;
    let displayHeader = true;
    let headerBackground = jnjFullBranded ? undefined : "primary";
    let hero = null;
    let carousel = null;
    let variant = null;
    const metaTitle = pageContext?.page?.seo_settings?.meta_title;

    let headerSize = header_visual.header_image_size || "auto";

    switch (header_visual.header_visual_source) {
        case CMS_HEADER_SOURCE_INHERIT_FROM_SITE:
            if (siteReference) {
                headerVisual = siteReference?.branding?.header_visual;
            } else {
                headerBackground = "transparent";
                headerSize = null;
            }
            break;
        case CMS_HEADER_SOURCE_IMAGE:
            headerVisual = header_visual.header_image_from_file;
            if (jnjFullBranded) {
                variant = header_visual?.variant;
                headerBackground = header_visual?.background_color?.toLowerCase();
            }
            break;
        case CMS_HEADER_SOURCE_HERO:
            hero = header_visual?.select_hero?.[0];
            if (jnjFullBranded) {
                variant = header_visual?.variant;
                headerBackground = hero?.display_options?.background_color?.toLowerCase();
            } else {
                // Overwrite the value of headersize for Hero component
                headerSize = hero?.display_options?.hero_height || "auto";
            }
            break;
        case CMS_HEADER_SOURCE_CAROUSEL:
            carousel = header_visual?.select_carousel?.[0];
            headerBackground = header_visual?.background_color?.toLowerCase();
            break;
        case CMS_HEADER_SOURCE_COLORED_BACKGROUND:
            if (jnjFullBranded) {
                headerBackground = header_visual?.background_color?.toLowerCase();
            }
            break;
        case CMS_HEADER_SOURCE_NONE:
            displayHeader = false;
            headerBackground = "transparent";
            break;
    }

    const isStartPage = !!siteReference.startpage && siteReference.startpage[0]?.uid === uid;

    const userProfiles = siteReference.access_control?.user_profiles;

    const renderBreadcrumb = (): JSX.Element => {
        if (
            props.data.page.regulatory_status.promotional_or_medical === CMS_MEDICAL_CONTENT ||
            pageContext.url == "/"
        ) {
            return;
        }

        const defaultServices = getDefaultServiceProperties();
        const siteName =
            (relatedTo as CMSProductProps)?.productTitle ||
            (relatedTo as CMSSpecialtyProps)?.specialtyTitle ||
            (relatedTo as CMSEventProps)?.eventTitle;

        const serviceUrl = (): string => {
            for (const [, value] of Object.entries<DefaultServiceProperties>(defaultServices)) {
                if (relatedTo?.__typename === value.relation) {
                    return value.url;
                }
            }
        };

        const serviceTitle = (): string => {
            for (const [, value] of Object.entries<DefaultServiceProperties>(defaultServices)) {
                if (relatedTo?.__typename === value.relation) {
                    switch (value.title) {
                        case "Products":
                            return t("Products", { ns: "common" });
                        case "Specialties":
                            return t("Specialties", { ns: "common" });
                        case "Events":
                            return t("Events", { ns: "common" });
                        default:
                            return value.title;
                    }
                }
            }
        };

        const startPageUrl = siteReference.startpage && siteReference.startpage[0]?.url;

        const paths = [];
        if (siteName) {
            let disabled = false;
            if ((relatedTo as CMSProductProps)?.productTitle && !productGlossary?.enable_service) {
                disabled = true;
            }
            if ((relatedTo as CMSSpecialtyProps)?.specialtyTitle && !specialtyService?.enable_service) {
                disabled = true;
            }
            paths.push({ url: serviceUrl(), title: serviceTitle(), disabled });
        }
        if (siteName && !isStartPage) {
            paths.push({ url: startPageUrl, title: siteName });
        }

        const renderedTitle = (isStartPage ? siteName || page_title : page_title) || title;

        paths.push({ url: url, title: renderedTitle });

        return <JMCBreadcrumb footnotes={fields?.footnotes} paths={[...paths]} />;
    };

    const l3 = commercial ? access_level : AccessLevel.level3;

    const hreflangTags =
        pageContext?.availableLocales?.length > 1
            ? {
                  locale: pageContext?.availableLocales,
                  url: pageContext?.url,
              }
            : null;

    const hasPaywall = (): boolean => {
        if (!yn(process.env.GATSBY_PAYWALL_ENABLED)) return false;
        if (seo_settings.paywall === "inherit") return siteReference?.meta_fields?.paywall;
        if (seo_settings.paywall === "on") return true;
        return false;
    };

    const showNoSnippet = (): boolean => {
        return hasPaywall() && l3 !== AccessLevel.level1;
    };

    const medicalExpert: CMSPersonProps =
        seo_settings?.medical_expert_reviewer === "Inherit from site"
            ? siteReference.meta_fields?.medical_expert?.[0]
            : seo_settings?.medical_expert?.[0];

    const getPaywallFragmentData = (): CMSFragmentProps => {
        if (seo_settings.paywall === "inherit") {
            return siteReference?.meta_fields?.site_paywall_teaser[0] || null;
        }
        if (seo_settings.paywall === "on") {
            return seo_settings?.paywall_teaser?.[0] || null;
        }
        return null;
    };

    const getPaywallTeaserTitle = (): string => {
        if (seo_settings.paywall === "inherit") {
            return siteReference?.meta_fields?.site_paywall_teaser[0]?.title || null;
        }
        if (seo_settings.paywall === "on") {
            return seo_settings?.paywall_teaser?.[0]?.title || null;
        }
        return null;
    };

    const getPaywallImrData = (): IMR[] => {
        if (seo_settings.paywall === "inherit") {
            return siteReference?.meta_fields?.site_teaser_imr?.imr || [];
        }
        if (seo_settings.paywall === "on") {
            return seo_settings?.teaser_imr_code?.imr || [];
        }
        return [];
    };

    const firstSectionColor = pageContext?.page?.firstSectionColor;

    const aepiDisclaimer = ae_pi_disclaimer && show_AE_PI_Disclaimer && jnjFullBranded && (
        <div className={style["aepi-disclaimer"]}>
            <JMCHtml data-test-id="aepi.disclaimer.text">{ae_pi_disclaimer}</JMCHtml>
        </div>
    );

    const removePaddingFirstSection =
        jnjFullBranded &&
        noTopPaddingFirstSection(
            firstSectionColor,
            headerBackground,
            variant,
            headerVisual
                ? headerVisual?.gatsbyImageData
                    ? headerVisual?.gatsbyImageData
                    : headerVisual?.url
                : hero?.background_image?.gatsbyImageData
                  ? hero?.background_image?.gatsbyImageData
                  : hero?.background_image?.url,
            null,
            !!(headerVisual || hero || page_title || carousel),
        );

    const Wrapper = (): JSX.Element => {
        const pageData = useContext(AccessContext);

        if (pageData === "displayPaywallFragment") {
            if (showPaywallTeaser === false) {
                setShowPaywallTeaser(true);
            }
            return (
                <PaywallTeaser
                    title={seo_settings?.meta_title}
                    description={seo_settings?.page_description}
                    code={getPaywallImrData()[0]}
                    fragment={getPaywallFragmentData()}
                    commercial={commercial}
                    teaserTitle={getPaywallTeaserTitle()}
                />
            );
        }

        return (
            <MedicalControl medicalContent={regulatory_status?.promotional_or_medical === CMS_MEDICAL_CONTENT}>
                <QuickEventsContext.Provider value={quickEvents}>
                    <Body
                        section={pageData?.content}
                        footnotes={fields?.footnotes}
                        placement={page_title}
                        locale={locale}
                        showToc={table_of_content}
                        cvents={cvents}
                        smpcs={[...(siteReference?.smpc_reference || []), ...(smpc_reference || [])]}
                        relatedProduct={isProductPage ? (relatedTo as CMSProductProps) : undefined}
                        marginXl
                        medicalExpert={medicalExpert}
                        noTopPaddingFirstSection={removePaddingFirstSection}
                    />
                </QuickEventsContext.Provider>
            </MedicalControl>
        );
    };

    const filterFAQPredicate = (component): boolean => {
        return component?.component?.[0]?.internal?.type === "Contentstack_component_faq";
    };

    const faqComponents =
        props?.data.page?.content?.length &&
        collectDeep(props?.data.page?.content, {}, false, filterFAQPredicate, null);

    const faqs = mapFaqSEO(faqComponents?.component?.map((c) => c.component?.[0]) as CMSFaqProps[]);

    return (
        <AuthDataProvider authData={authData} accessLevel={access_level}>
            <SEO
                title={metaTitle !== "null" && metaTitle ? metaTitle : pageContext?.page?.title}
                description={pageContext?.page?.seo_settings?.meta_description}
                canonical={pageContext?.page?.seo_settings?.canonical_tag?.replace(/\/+$/, "")}
                hreflangs={pageContext?.page?.seo_settings?.hreflang_associated_url}
                clonesHreflangs={pageContext?.page?.seo_settings?.clonesHreflangs}
                availableLocale={hreflangTags}
                level={l3}
                index={seo_settings?.indexing_and_sitemap || "default"}
                context={pageContext}
                paywall={hasPaywall()}
                currentPageUrl={pageContext?.url}
                open_graph={seo_settings?.open_graph}
                twitter={seo_settings.twitter}
                uid={uid}
                contentType="page"
                noIndexImages={showNoSnippet() && !showPaywallTeaser}
                faqs={faqs.length && faqs}
            />
            <FootnotesProvider>
                <div
                    data-test-id="page-content-snippet"
                    style={showPaywallTeaser ? { display: "flex", flexGrow: 1 } : {}}
                    // eslint-disable-next-line react/jsx-props-no-spreading
                    {...(showNoSnippet() && !showPaywallTeaser ? { "data-nosnippet": true, className: "paywall" } : {})}
                >
                    {(displayHeader || (!displayHeader && page_title && page_title !== "")) && (
                        <PageHeader
                            placement={page_title}
                            renderBreadcrumb={renderBreadcrumb}
                            headerVisual={
                                headerVisual?.gatsbyImageData ? headerVisual?.gatsbyImageData : headerVisual?.url
                            }
                            // Deliberately NOT falling back to title: https://jira.jnj.com/browse/AFWN-6986
                            title={
                                page_title && page_title !== "" ? (
                                    <JMCHtml footnotes={fields?.footnotes}>{page_title}</JMCHtml>
                                ) : null
                            }
                            headerSize={headerSize as "Extra Small" | "Small" | "Large" | "Medium" | "auto"}
                            headerBackground={headerBackground as HEADING_BACKGROUNDS}
                            variant={variant}
                            relatedTo={relatedTo}
                            hero={hero}
                            carousel={carousel}
                            imageFocus={
                                header_visual?.image_focus?.image_focus?.toLowerCase() ||
                                siteReference?.branding?.image_focus?.image_focus?.toLowerCase() ||
                                "center-center"
                            }
                            marginXl
                            shouldAddProductTheme={shouldAddProductTheme}
                            mobilePadding={true}
                            footnotes={fields?.footnotes}
                            firstSectionColor={firstSectionColor}
                            bannerDisclaimer={aepiDisclaimer}
                            hasNextSectionEqualColor={removePaddingFirstSection}
                        />
                    )}
                    {ae_pi_disclaimer && show_AE_PI_Disclaimer && !jnjFullBranded && (
                        <div className={style.aepiDisclaimer}>
                            <FlexibleGrid fractions={[1]} minHeight={false}>
                                <SnackBar>
                                    <SnackBar.Icon icon={mdiInformationOutline} size="small" />
                                    <JMCHtml data-test-id="aepi.disclaimer.text">{ae_pi_disclaimer}</JMCHtml>
                                </SnackBar>
                            </FlexibleGrid>
                        </div>
                    )}

                    <AccessControl
                        gatedUrl={gatedUrl}
                        content={content}
                        // Medical content should always be level3
                        level={commercial ? access_level : AccessLevel.level3}
                        combinedMedical={regulatory_status?.promotional_or_medical === CMS_MEDICAL_CONTENT}
                        allowedUserProfiles={userProfiles}
                        paywall={hasPaywall()}
                        title={page_title}
                    >
                        <Wrapper />
                    </AccessControl>
                </div>
            </FootnotesProvider>
        </AuthDataProvider>
    );
};

export default FreeFormPage;
